import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp, ArrowDown, Camera, X, Mic, Square, Loader2, Zap, Paperclip, ThumbsUp, ThumbsDown, Globe, FileText, Download, FlaskConical, Volume2, StopCircle, Heart, Check, FolderOpen, CalendarPlus, ExternalLink, Repeat, Clock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { nexaApi, CHAT_URL, API_ROOT, getKey, photoUrl, fileUrl } from './api';

const MAX_PHOTOS = 5;

const fmtEventWhen = (start, end) => {
  if (!start) return '';
  const allDay = start.length === 10;
  try {
    const s = new Date(start);
    const dateStr = s.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    if (allDay) return `${dateStr} · all day`;
    const t = (d) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const endStr = end ? `–${t(new Date(end))}` : '';
    return `${dateStr} · ${t(s)}${endStr}`;
  } catch {
    return start;
  }
};

const EventCard = ({ ev, testId }) => {
  const allDay = (ev.start || '').length === 10;
  const [when, setWhen] = useState({ start: ev.start, end: ev.end });
  const [cancelled, setCancelled] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const clip = (v) => (allDay ? (v || '').slice(0, 10) : (v || '').slice(0, 16));
  const [draft, setDraft] = useState({ start: clip(ev.start), end: clip(ev.end) });

  const cancelEvent = async () => {
    if (busy) return;
    if (!window.confirm(`Cancel "${ev.title}"? This removes it from your Google Calendar.`)) return;
    setBusy(true);
    try {
      await nexaApi.delete(`/google/events/${ev.id}`);
      setCancelled(true);
      toast.success('Event cancelled');
    } catch (e) {
      toast.error('Could not cancel: ' + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const saveReschedule = async () => {
    if (busy) return;
    if (!draft.start || !draft.end) { toast.error('Pick a start and end time'); return; }
    setBusy(true);
    try {
      const norm = (v) => (v.length === 16 ? `${v}:00` : v);
      const start = norm(draft.start); const end = norm(draft.end);
      await nexaApi.patch(`/google/events/${ev.id}`, { start, end });
      setWhen({ start, end });
      setEditing(false);
      toast.success('Event rescheduled');
    } catch (e) {
      toast.error('Could not reschedule: ' + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const inputType = allDay ? 'date' : 'datetime-local';

  return (
    <div
      data-testid={testId}
      className={`w-72 max-w-full rounded-2xl border p-3 transition-colors ${
        cancelled ? 'border-white/10 bg-white/[0.03] opacity-60'
                  : 'border-sky-400/25 bg-sky-400/[0.06]'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 shrink-0 rounded-xl flex items-center justify-center ${
          cancelled ? 'bg-white/10' : 'bg-sky-400/15'}`}>
          <CalendarPlus className={`w-4 h-4 ${cancelled ? 'text-white/40' : 'text-sky-300'}`} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-mono uppercase tracking-[0.14em] flex items-center gap-1.5 text-sky-300/70">
            {cancelled ? <span className="text-white/40">Cancelled</span> : 'Added to Calendar'}
            {ev.recurring && !cancelled && <Repeat className="w-3 h-3" strokeWidth={2.2} />}
          </div>
          <div className={`text-sm font-medium truncate mt-0.5 ${cancelled ? 'text-white/40 line-through' : 'text-white/90'}`}>
            {ev.title}
          </div>
          <div className="text-[11px] text-white/50 mt-0.5">{fmtEventWhen(when.start, when.end)}</div>
        </div>
        {ev.html_link && !cancelled && (
          <a href={ev.html_link} target="_blank" rel="noopener noreferrer"
             data-testid={`${testId}-open`} title="Open in Google Calendar"
             className="text-white/30 hover:text-sky-300 shrink-0 mt-0.5">
            <ExternalLink className="w-3.5 h-3.5" strokeWidth={2} />
          </a>
        )}
      </div>

      {!cancelled && editing && (
        <div className="mt-3 flex flex-col gap-2">
          <label className="text-[10px] uppercase tracking-wider text-white/40">Start</label>
          <input type={inputType} value={draft.start}
            onChange={(e) => setDraft((d) => ({ ...d, start: e.target.value }))}
            data-testid={`${testId}-start-input`}
            className="bg-black/30 border border-white/15 rounded-lg px-2 py-1.5 text-sm text-white/90 outline-none focus:border-sky-400/50" />
          <label className="text-[10px] uppercase tracking-wider text-white/40">End</label>
          <input type={inputType} value={draft.end}
            onChange={(e) => setDraft((d) => ({ ...d, end: e.target.value }))}
            data-testid={`${testId}-end-input`}
            className="bg-black/30 border border-white/15 rounded-lg px-2 py-1.5 text-sm text-white/90 outline-none focus:border-sky-400/50" />
          <div className="flex items-center gap-2 mt-1">
            <button onClick={saveReschedule} disabled={busy} data-testid={`${testId}-save`}
              className="flex-1 h-8 rounded-lg bg-sky-400 text-black text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-95 transition">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" strokeWidth={3} />} Save
            </button>
            <button onClick={() => { setEditing(false); setDraft({ start: clip(when.start), end: clip(when.end) }); }}
              disabled={busy} data-testid={`${testId}-cancel-edit`}
              className="h-8 px-3 rounded-lg border border-white/15 text-white/60 text-xs active:scale-95 transition">
              <X className="w-3.5 h-3.5" strokeWidth={2.2} />
            </button>
          </div>
        </div>
      )}

      {!cancelled && !editing && (
        <div className="mt-2.5 flex items-center gap-2">
          <button onClick={() => setEditing(true)} data-testid={`${testId}-reschedule`}
            className="flex-1 h-8 rounded-lg border border-white/15 text-white/70 hover:text-sky-300 hover:border-sky-400/40 text-xs flex items-center justify-center gap-1.5 active:scale-95 transition">
            <Clock className="w-3.5 h-3.5" strokeWidth={2} /> Reschedule
          </button>
          <button onClick={cancelEvent} disabled={busy} data-testid={`${testId}-cancel`}
            className="flex-1 h-8 rounded-lg border border-white/15 text-white/70 hover:text-rose-300 hover:border-rose-400/40 text-xs flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-95 transition">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />} Cancel
          </button>
        </div>
      )}
    </div>
  );
};



const resizeToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Higher cap + quality so NEXA can read fine text and small details.
      const scale = Math.min(1, 2000 / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL('image/jpeg', 0.92).split(',')[1]);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

const renderItalics = (text, keyBase) => {
  const out = [];
  const re = /\*([^*\n]+)\*/g;
  let last = 0;
  let m;
  let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1][0] !== ' ' && m[1][m[1].length - 1] !== ' ') {
      out.push(<em key={`${keyBase}-i${k}`}>{m[1]}</em>);
    } else {
      out.push(m[0]);
    }
    k += 1;
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

const renderRich = (text) => {
  if (!text || (!text.includes('*') && !text.includes('#'))) return text;
  const clean = String(text).replace(/^#{1,4}\s+/gm, '');
  const out = [];
  const re = /\*\*([^\n]+?)\*\*/g;
  let last = 0;
  let m;
  let k = 0;
  while ((m = re.exec(clean)) !== null) {
    if (m.index > last) out.push(...renderItalics(clean.slice(last, m.index), `s${k}`));
    out.push(
      <strong key={`b${k}`} className="font-semibold text-white">
        {renderItalics(m[1], `b${k}`)}
      </strong>
    );
    k += 1;
    last = m.index + m[0].length;
  }
  if (last < clean.length) out.push(...renderItalics(clean.slice(last), `t${k}`));
  return out;
};

export const ChatView = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [maxMode, setMaxMode] = useState(() => localStorage.getItem('nexa_max_mode') === '1');
  const [personalityMode, setPersonalityMode] = useState(() => localStorage.getItem('nexa_personality_mode') === '1');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docDialog, setDocDialog] = useState(null); // {status:'uploading'|'ready', doc, category, categories, filename}
  const [feedbackFor, setFeedbackFor] = useState(null);
  const [feedbackReason, setFeedbackReason] = useState('');
  const [ttsState, setTtsState] = useState({ idx: null, status: 'idle' }); // status: 'loading' | 'playing' | 'idle'
  const audioRef = useRef(null);
  const recorderRef = useRef(null);

  const stopTts = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    setTtsState({ idx: null, status: 'idle' });
  };

  const playTts = async (text, idx) => {
    if (ttsState.idx === idx && ttsState.status !== 'idle') {
      stopTts();
      return;
    }
    stopTts();
    setTtsState({ idx, status: 'loading' });
    try {
      const res = await fetch(`${API_ROOT}/api/nexa/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-nexa-key': getKey() },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error('TTS failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        URL.revokeObjectURL(url);
        setTtsState((s) => (s.idx === idx ? { idx: null, status: 'idle' } : s));
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        setTtsState({ idx: null, status: 'idle' });
        toast.error('Could not play audio');
      };
      await audio.play();
      setTtsState({ idx, status: 'playing' });
    } catch {
      setTtsState({ idx: null, status: 'idle' });
      toast.error('Could not read that out');
    }
  };

  const sendFeedback = async (msg, value, reason, idx) => {
    setMessages((m) => m.map((x, i) => (i === idx ? { ...x, feedback: value } : x)));
    setFeedbackFor(null);
    setFeedbackReason('');
    try {
      await nexaApi.post('/chat/feedback', { message_id: msg.id, value, reason: reason || null });
      toast.success(value > 0 ? 'Noted — glad it helped' : 'Noted — she\'ll learn from this');
    } catch {
      toast.error('Could not send feedback');
    }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    setUploadingDoc(true);
    setDocDialog({ status: 'uploading', filename: file.name || 'file' });
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onloadend = () => resolve(String(r.result).split(',')[1]);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const fname = file.name || 'document';
      // Chunked upload: large files exceed proxy body limits as one request,
      // so send ~500KB parts and let the backend assemble (same as voice).
      const CHUNK = 500 * 1024;
      const total = Math.max(1, Math.ceil(b64.length / CHUNK));
      const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      let data = null;
      for (let i = 0; i < total; i += 1) {
        const resp = await nexaApi.post('/docs/upload/chunk', {
          upload_id: uploadId,
          index: i,
          total,
          data: b64.slice(i * CHUNK, (i + 1) * CHUNK),
          filename: fname,
        });
        data = resp.data;
      }
      setDocDialog({
        status: 'ready',
        doc: data.doc,
        category: data.doc.category || 'General',
        categories: data.categories || [],
        filename: data.doc.filename,
      });
    } catch (err) {
      setDocDialog({
        status: 'error',
        filename: file.name || 'file',
        error: err.response?.data?.detail || 'Could not read the file. Please try again.',
      });
    } finally {
      setUploadingDoc(false);
    }
  };

  const cancelDoc = async () => {
    const doc = docDialog?.doc;
    setDocDialog(null);
    if (doc?.id) {
      try { await nexaApi.delete(`/docs/${doc.id}`); } catch { /* ignore */ }
    }
  };

  const confirmDoc = async () => {
    if (!docDialog?.doc) return;
    const { doc, category, filename } = docDialog;
    setDocDialog((d) => ({ ...d, status: 'saving' }));
    try {
      const { data } = await nexaApi.patch(`/docs/${doc.id}`, {
        category: (category || 'General').trim(),
        filename: (filename || doc.filename).trim(),
      });
      setMessages((m) => [...m, { role: 'assistant', content: data.message }]);
      toast.success('Added to her memory');
    } catch {
      toast.error('Could not save the file');
    } finally {
      setDocDialog(null);
    }
  };

  const toggleMax = () => {
    setMaxMode((v) => {
      localStorage.setItem('nexa_max_mode', v ? '0' : '1');
      return !v;
    });
  };
  const togglePersonality = () => {
    setPersonalityMode((v) => {
      localStorage.setItem('nexa_personality_mode', v ? '0' : '1');
      return !v;
    });
  };
  const chunksRef = useRef([]);
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);
  const photoInputRef = useRef(null);
  const docInputRef = useRef(null);
  const [atBottom, setAtBottom] = useState(true);
  const [dragActive, setDragActive] = useState(false);

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    // Instant multi-pass jump: smooth scrolling on iOS is unreliable over long
    // content and triggers the blank-render bug; late passes catch layout
    // shifts from images/textarea resize.
    const jump = () => { el.scrollTop = el.scrollHeight; };
    jump();
    requestAnimationFrame(jump);
    setTimeout(jump, 120);
    setTimeout(jump, 400);
  };

  // Track how far Lev has scrolled up from the newest message.
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    setAtBottom(distance < 48);
  };

  useEffect(() => {
    nexaApi.get('/chat/history').then(({ data }) => setMessages(data.messages));
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const streamingRef = useRef(false);
  useEffect(() => { streamingRef.current = streaming; }, [streaming]);

  const recoverTimer = useRef(null);
  const recoverReply = () => {
    if (recoverTimer.current) clearInterval(recoverTimer.current);
    let tries = 0;
    const attempt = async () => {
      tries += 1;
      try {
        const { data } = await nexaApi.get('/chat/history');
        const msgs = data.messages || [];
        if (msgs.length && msgs[msgs.length - 1].role === 'assistant') {
          setMessages(msgs);
          clearInterval(recoverTimer.current);
          recoverTimer.current = null;
        } else if (tries > 40) {
          clearInterval(recoverTimer.current);
          recoverTimer.current = null;
        }
      } catch { /* keep trying */ }
    };
    attempt();
    recoverTimer.current = setInterval(attempt, 3000);
  };

  useEffect(() => {
    // iOS leaves the page shifted after the keyboard closes — snap back.
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const onResize = () => {
      if (vv.height >= window.innerHeight - 60) window.scrollTo(0, 0);
    };
    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    // When Lev leaves mid-reply, the backend keeps generating and saves the full
    // answer. On return, re-sync from the server and poll briefly if she is
    // still thinking so the finished reply appears without a manual refresh.
    let pollTimer = null;
    const stopPoll = () => { if (pollTimer) { clearInterval(pollTimer); pollTimer = null; } };
    const refresh = async () => {
      if (streamingRef.current) return null;
      try {
        const { data } = await nexaApi.get('/chat/history');
        if (!streamingRef.current) setMessages(data.messages);
        return data.messages;
      } catch { return null; }
    };
    const onVis = () => {
      if (document.visibilityState !== 'visible') { stopPoll(); return; }
      // Give a dead fetch a moment to throw before deciding the stream is live.
      setTimeout(async () => {
        if (streamingRef.current) return;
        const msgs = await refresh();
        if (msgs && msgs.length && msgs[msgs.length - 1].role === 'user') {
          let tries = 0;
          stopPoll();
          pollTimer = setInterval(async () => {
            tries += 1;
            const m = await refresh();
            if (!m || tries > 30 || (m.length && m[m.length - 1].role !== 'user')) stopPoll();
          }, 4000);
        }
      }, 1200);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => { stopPoll(); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  useEffect(() => {
    // Only auto-follow when Lev is already near the bottom. If he scrolled
    // up to re-read something, don't yank him back — the floating "Jump to
    // latest" pill lets him rejoin on demand.
    if (atBottom) {
      scrollToBottom();
    }
  }, [messages, streaming, atBottom]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    setStreaming(true);
    setMessages((m) => [...m, { role: 'user', content: text }, { role: 'assistant', content: '' }]);

    try {
      const res = await fetch(CHAT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-nexa-key': getKey() },
        body: JSON.stringify({ message: text, session_id: 'main', max_mode: maxMode, personality_mode: personalityMode }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          const evt = JSON.parse(part.slice(6));
          if (evt.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                ...copy[copy.length - 1],
                content: copy[copy.length - 1].content + evt.delta,
              };
              return copy;
            });
          }
          if (evt.model === 'max') {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], max: true };
              return copy;
            });
          }
          if (evt.mode === 'personality') {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], personality: true };
              return copy;
            });
          }
          if (evt.searching !== undefined) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], searching: evt.searching };
              return copy;
            });
          }
          if (evt.researching !== undefined) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], researching: evt.researching };
              return copy;
            });
          }
          if (evt.creating_file !== undefined) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], creatingFile: Boolean(evt.creating_file) };
              return copy;
            });
          }
          if (evt.file !== undefined) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], creatingFile: false, file: evt.file };
              return copy;
            });
          }
          if (evt.msg_id) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], id: evt.msg_id };
              return copy;
            });
          }
          if (evt.imagining !== undefined) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], imagining: true };
              return copy;
            });
          }
          if (evt.image !== undefined) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                ...copy[copy.length - 1], imagining: false, photo_id: evt.image,
              };
              return copy;
            });
          }
          if (evt.events !== undefined) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                ...copy[copy.length - 1], event_cards: evt.events,
              };
              return copy;
            });
          }
          if (evt.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: 'assistant', content: 'Something went wrong — try again.' };
              return copy;
            });
          }
        }
      }
    } catch {
      // The connection died, but the backend keeps generating and saves the
      // full reply — recover it from the server instead of giving up.
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: 'assistant', content: 'Connection dropped — recovering her answer…' };
        return copy;
      });
      recoverReply();
    } finally {
      setStreaming(false);
    }
  };

  const sendPhotos = async () => {
    const files = attachments;
    const note = input.trim();
    if (!files.length || streaming) return;
    setInput('');
    setAttachments([]);
    setStreaming(true);
    const label = `📷 ${files.length === 1 ? 'Sent a photo' : `Sent ${files.length} photos`}${note ? ` — ${note}` : ''}`;
    setMessages((m) => [
      ...m,
      { role: 'user', content: label, images: files.map((f) => f.url) },
      { role: 'assistant', content: '' },
    ]);
    try {
      const images_base64 = await Promise.all(files.map((f) => resizeToBase64(f.file)));
      // Chunked send: several photos in one request exceed proxy body limits,
      // so send the payload in 500KB parts and assemble on the backend.
      const payload = JSON.stringify({ images_base64, note: note || null, personality_mode: personalityMode });
      const b64 = btoa(unescape(encodeURIComponent(payload)));
      const CHUNK = 500 * 1024;
      const total = Math.max(1, Math.ceil(b64.length / CHUNK));
      const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      let data = null;
      for (let i = 0; i < total; i += 1) {
        const resp = await nexaApi.post('/photo/chunk', {
          upload_id: uploadId, index: i, total, data: b64.slice(i * CHUNK, (i + 1) * CHUNK),
        });
        data = resp.data;
      }
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: 'assistant', content: data.message, personality: data.personality };
        return copy;
      });
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: 'assistant', content: 'Could not analyze the photos — try again.' };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    setAttachments((prev) => {
      const room = MAX_PHOTOS - prev.length;
      const next = incoming.slice(0, room).map((file) => ({
        id: `${Date.now()}-${Math.random()}`,
        file,
        url: URL.createObjectURL(file),
      }));
      return [...prev, ...next];
    });
  };

  // Unified intake for paste / drag-drop / pickers: images become photo
  // attachments, PDFs & other docs go through the document upload flow.
  const handleIncomingFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const images = files.filter((f) => f.type.startsWith('image/'));
    const docs = files.filter((f) => !f.type.startsWith('image/'));
    if (images.length) addFiles(images);
    if (docs.length) {
      uploadDoc(docs[0]);
      if (docs.length > 1) toast.info('One document at a time — added the first, send it, then add the next.');
    }
  };

  const onPasteFiles = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (let i = 0; i < items.length; i += 1) {
      if (items[i].kind === 'file') {
        const f = items[i].getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length) {
      e.preventDefault();
      handleIncomingFiles(files);
    }
  };

  const dragDepth = useRef(0);
  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const onDragEnter = (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragActive(true);
  };
  const onDragOver = (e) => { if (hasFiles(e)) e.preventDefault(); };
  const onDragLeave = () => {
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) { dragDepth.current = 0; setDragActive(false); }
  };
  const onDropFiles = (e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragActive(false);
    if (e.dataTransfer?.files?.length) handleIncomingFiles(e.dataTransfer.files);
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => {
      const hit = prev.find((a) => a.id === id);
      if (hit) URL.revokeObjectURL(hit.url);
      return prev.filter((a) => a.id !== id);
    });
  };

  const handleSend = () => {
    if (attachments.length) sendPhotos();
    else send();
  };

  const toggleRecord = async () => {
    if (transcribing) return;
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      alert('Voice input not supported on this browser.');
      return;
    }
    // Free the audio session for the mic: stop TTS playback and any site
    // audio (iOS won't record while another player holds the session).
    stopTts();
    try { window.dispatchEvent(new Event('lv-music-pause')); } catch { /* noop */ }
    document.querySelectorAll('audio, video').forEach((el) => { try { el.pause(); } catch { /* noop */ } });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/mpeg'];
      const mime = mimes.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || '';
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        if (blob.size < 800) return; // ignore accidental taps
        setTranscribing(true);
        try {
          const b64 = await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onloadend = () => resolve(String(r.result).split(',')[1]);
            r.onerror = reject;
            r.readAsDataURL(blob);
          });
          // Chunked upload: long recordings exceed proxy body limits (413) as a
          // single request, so send ~500KB parts and let the backend assemble.
          const CHUNK = 500 * 1024;
          const total = Math.max(1, Math.ceil(b64.length / CHUNK));
          const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
          let data = null;
          for (let i = 0; i < total; i += 1) {
            const resp = await nexaApi.post('/chat/transcribe/chunk', {
              upload_id: uploadId,
              index: i,
              total,
              data: b64.slice(i * CHUNK, (i + 1) * CHUNK),
              mime: blob.type,
            });
            data = resp.data;
          }
          const t = (data.text || '').trim();
          if (t) setInput((prev) => (prev ? prev + ' ' + t : t));
        } catch (err) {
          alert('Transcription failed: ' + (err.response?.data?.detail || err.message));
        } finally {
          setTranscribing(false);
        }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (err) {
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        alert('Microphone access is blocked. Enable it in iPhone Settings → Safari (or the NEXA app) → Microphone.');
      } else if (err?.name === 'NotFoundError') {
        alert('No microphone found on this device.');
      } else {
        alert(`Could not start recording (${err?.name || 'unknown'}). Close other apps using the mic and try again.`);
      }
    }
  };

  return (
    <div
      className="flex flex-col h-full min-h-0 relative"
      data-testid="nexa-chat-view"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDropFiles}
    >
      <AnimatePresence>
        {dragActive && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="nexa-chat-dropzone"
            className="absolute inset-2 z-40 rounded-3xl border-2 border-dashed border-sky-400/60 bg-sky-400/[0.08] backdrop-blur-sm flex flex-col items-center justify-center gap-2 pointer-events-none"
          >
            <Paperclip className="w-7 h-7 text-sky-300" strokeWidth={1.8} />
            <p className="text-sky-100 text-sm font-medium">Drop a photo or PDF for NEXA</p>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            data-testid="nexa-chat-lightbox"
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <img src={photoUrl(lightbox)} alt="" className="max-w-full max-h-full rounded-2xl object-contain" />
            <button aria-label="Close" className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white">
              <X className="w-5 h-5" strokeWidth={2} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      <div ref={scrollRef} onScroll={onScroll}
        className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col gap-5 pb-4"
        style={{ WebkitTransform: 'translateZ(0)', overscrollBehaviorX: 'none', touchAction: 'pan-y' }}>
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center flex-1 text-center px-8 pt-20">
            <img src="/nexa-logo.png" alt="NEXA" className="w-16 h-16 mb-4 select-none" draggable="false" />
            <p className="nexa-heading text-xl font-light">Talk to NEXA</p>
            <p className="text-sm text-white/40 mt-2">
              She knows your routine and weight history. Ask her anything — &ldquo;how am I doing this week?&rdquo;
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'self-end max-w-[85%]' : 'self-start max-w-[95%]'}>
            {m.role === 'user' ? (
              <div className="bg-white/10 rounded-2xl rounded-br-md px-4 py-3 text-[15px] leading-relaxed">
                {m.images?.length > 0 && (
                  <div className="flex gap-1.5 mb-2 flex-wrap">
                    {m.images.map((url, j) => (
                      <img key={j} src={url} alt="" className="w-16 h-16 rounded-lg object-cover" />
                    ))}
                  </div>
                )}
                {renderRich(m.content)}
              </div>
            ) : (
              <div className="flex gap-3">
                <img src="/nexa-logo.png" alt="" className="w-5 h-5 mt-1 shrink-0 select-none" draggable="false" />
                <div className="text-[15px] leading-relaxed text-white/90 whitespace-pre-wrap break-words">
                  {m.max && (
                    <span className="inline-flex items-center gap-1 mb-1.5 mr-2 px-2 py-0.5 rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-300 text-[9px] font-mono tracking-widest align-middle"
                      data-testid="nexa-max-badge">
                      <Zap className="w-2.5 h-2.5" strokeWidth={2.5} fill="currentColor" />
                      MAX
                    </span>
                  )}
                  {m.personality && (
                    <span className="inline-flex items-center gap-1 mb-1.5 mr-2 px-2 py-0.5 rounded-full border border-rose-400/30 bg-rose-400/10 text-rose-300 text-[9px] font-mono tracking-widest align-middle"
                      data-testid="nexa-personality-badge">
                      <Heart className="w-2.5 h-2.5" strokeWidth={2.5} fill="currentColor" />
                      SOUL
                    </span>
                  )}
                  {(m.content && renderRich(m.content)) || (
                    <span className="inline-flex gap-1 items-center h-5">
                      <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse" />
                      <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse [animation-delay:150ms]" />
                      <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse [animation-delay:300ms]" />
                    </span>
                  )}
                  {m.searching && (
                    <span className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-sky-400/20 bg-white/[0.04] animate-pulse text-[11px] font-mono tracking-widest text-sky-400/70"
                      data-testid="nexa-web-searching">
                      <Globe className="w-3.5 h-3.5" strokeWidth={2} />
                      SEARCHING THE WEB…
                    </span>
                  )}
                  {m.researching && (
                    <span className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-violet-400/25 bg-white/[0.04] animate-pulse text-[11px] font-mono tracking-widest text-violet-300/80"
                      data-testid="nexa-researching">
                      <FlaskConical className="w-3.5 h-3.5" strokeWidth={2} />
                      {String(m.researching).toUpperCase()}
                    </span>
                  )}
                  {m.creatingFile && (
                    <span className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-sky-400/20 bg-white/[0.04] animate-pulse text-[11px] font-mono tracking-widest text-sky-400/70"
                      data-testid="nexa-file-creating">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2} />
                      CREATING FILE…
                    </span>
                  )}
                  {m.file && (
                    <a
                      href={fileUrl(m.file.id)}
                      download={m.file.name}
                      data-testid={`nexa-chat-file-${m.file.id}`}
                      className="mt-3 inline-flex items-center gap-2 px-4 py-3 rounded-2xl border border-sky-400/25 bg-sky-400/[0.08] text-sky-300 text-sm font-medium active:scale-95 transition-transform"
                    >
                      <FileText className="w-4 h-4 shrink-0" strokeWidth={2} />
                      {m.file.name}
                      <Download className="w-3.5 h-3.5 opacity-60 shrink-0" strokeWidth={2} />
                    </a>
                  )}
                  {m.imagining && (
                    <span className="block mt-3 w-44 h-56 rounded-2xl border border-sky-400/20 bg-white/[0.04] animate-pulse flex items-center justify-center text-[11px] font-mono tracking-widest text-sky-400/60"
                      data-testid="nexa-image-loading">
                      CREATING EXAMPLE…
                    </span>
                  )}
                  {m.photo_id && (
                    <img
                      src={photoUrl(m.photo_id)}
                      alt="Outfit example"
                      loading="lazy"
                      data-testid={`nexa-chat-image-${m.photo_id}`}
                      onClick={() => setLightbox(m.photo_id)}
                      className="block mt-3 w-52 rounded-2xl border border-white/10 cursor-pointer active:scale-95 transition-transform"
                    />
                  )}
                  {m.event_cards?.length > 0 && (
                    <div className="mt-3 flex flex-col gap-2" data-testid={`nexa-event-cards-${i}`}>
                      {m.event_cards.map((ev, j) => (
                        <EventCard key={ev.id || j} ev={ev} testId={`nexa-event-card-${i}-${j}`} />
                      ))}
                    </div>
                  )}
                  {m.id && m.content && !streaming && (
                    <span className="flex items-center gap-1 mt-2 -ml-1">
                      <button
                        type="button"
                        onClick={() => playTts(m.content, i)}
                        data-testid={`nexa-tts-play-${i}`}
                        aria-label={ttsState.idx === i && ttsState.status !== 'idle' ? 'Stop reading' : 'Read message aloud'}
                        title={ttsState.idx === i && ttsState.status !== 'idle' ? 'Stop reading' : 'Read message'}
                        className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                          ttsState.idx === i && ttsState.status !== 'idle'
                            ? 'text-sky-400'
                            : 'text-white/20 hover:text-white/50'
                        }`}
                      >
                        {ttsState.idx === i && ttsState.status === 'loading' ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2} />
                        ) : ttsState.idx === i && ttsState.status === 'playing' ? (
                          <StopCircle className="w-3.5 h-3.5" strokeWidth={2} />
                        ) : (
                          <Volume2 className="w-3.5 h-3.5" strokeWidth={2} />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => sendFeedback(m, 1, null, i)}
                        data-testid={`nexa-feedback-up-${i}`}
                        aria-label="Good reply"
                        className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                          m.feedback === 1 ? 'text-sky-400' : 'text-white/20 hover:text-white/50'
                        }`}
                      >
                        <ThumbsUp className="w-3.5 h-3.5" strokeWidth={2} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setFeedbackFor(feedbackFor === m.id ? null : m.id)}
                        data-testid={`nexa-feedback-down-${i}`}
                        aria-label="Bad reply"
                        className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                          m.feedback === -1 ? 'text-red-400' : 'text-white/20 hover:text-white/50'
                        }`}
                      >
                        <ThumbsDown className="w-3.5 h-3.5" strokeWidth={2} />
                      </button>
                    </span>
                  )}
                  {feedbackFor === m.id && (
                    <span className="flex items-center gap-2 mt-1" data-testid="nexa-feedback-reason-row">
                      <input
                        value={feedbackReason}
                        onChange={(e) => setFeedbackReason(e.target.value)}
                        placeholder="What was off? (optional)"
                        data-testid="nexa-feedback-reason-input"
                        className="flex-1 bg-white/[0.05] border border-white/10 rounded-xl px-3 py-1.5 text-[12px] outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => sendFeedback(m, -1, feedbackReason.trim(), i)}
                        data-testid="nexa-feedback-reason-send"
                        className="px-3 py-1.5 rounded-xl bg-white/10 text-[12px] text-white/80"
                      >
                        Send
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="sticky bottom-0 pt-2 pb-1 lg:pb-6 bg-black">
        {!atBottom && messages.length > 0 && (
          <button
            type="button"
            onClick={() => { setAtBottom(true); scrollToBottom(); }}
            data-testid="nexa-jump-to-latest"
            aria-label="Jump to latest message"
            className="absolute -top-12 left-1/2 -translate-x-1/2 flex items-center gap-1.5 h-9 px-4 rounded-full bg-sky-400 text-black text-[12px] font-semibold shadow-[0_6px_24px_rgba(56,189,248,0.35)] active:scale-95 transition-transform"
          >
            <ArrowDown className="w-3.5 h-3.5" strokeWidth={2.5} />
            Jump to latest
          </button>
        )}
        <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-2">
          {attachments.length > 0 && (
            <div className="flex gap-2 px-1 pt-1 pb-2 flex-wrap" data-testid="nexa-attachment-strip">
              {attachments.map((a) => (
                <div key={a.id} className="relative">
                  <img src={a.url} alt="" className="w-14 h-14 rounded-xl object-cover border border-white/10" />
                  <button
                    type="button"
                    onClick={() => removeAttachment(a.id)}
                    data-testid={`nexa-attachment-remove-${a.id}`}
                    aria-label="Remove photo"
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black border border-white/25 flex items-center justify-center text-white/80"
                  >
                    <X className="w-3 h-3" strokeWidth={2.5} />
                  </button>
                </div>
              ))}
              <span className="self-end text-[10px] font-mono text-white/30 pb-1">
                {attachments.length}/{MAX_PHOTOS}
              </span>
            </div>
          )}
          <div className="flex flex-wrap items-end gap-1.5">
            <input ref={photoInputRef} type="file" accept="image/*" multiple
              className="hidden" data-testid="nexa-photo-input"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
            <input ref={docInputRef} type="file"
              accept="application/pdf,.pdf,text/plain,.txt,.md,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx,text/csv,.csv,application/json,.json"
              className="hidden" data-testid="nexa-doc-input"
              onChange={(e) => { uploadDoc(e.target.files?.[0]); e.target.value = ''; }} />
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              disabled={attachments.length >= MAX_PHOTOS}
              className={`w-9 h-9 rounded-xl border border-white/10 flex items-center justify-center text-sky-400 shrink-0 active:scale-90 transition-transform ${
                attachments.length >= MAX_PHOTOS ? 'opacity-30 pointer-events-none' : ''
              }`}
              data-testid="nexa-photo-btn"
            >
              <Camera className="w-5 h-5" strokeWidth={1.8} />
            </button>
            <button
              type="button"
              onClick={() => docInputRef.current?.click()}
              disabled={uploadingDoc}
              className={`w-9 h-9 rounded-xl border border-white/10 flex items-center justify-center text-sky-400 shrink-0 active:scale-90 transition-transform ${
                uploadingDoc ? 'opacity-40 pointer-events-none' : ''
              }`}
              data-testid="nexa-doc-btn"
              title="Upload a file (PDF, TXT, MD, DOCX, XLSX, CSV)"
            >
              {uploadingDoc ? (
                <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.8} />
              ) : (
                <Paperclip className="w-5 h-5" strokeWidth={1.8} />
              )}
            </button>
            <textarea
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onPaste={onPasteFiles}
              onKeyDown={(e) => {
                // Touch devices (phones): let Enter add a new line, send via the button.
                // Desktop: keep Enter to send, Shift+Enter for newline.
                const isTouch = typeof window !== 'undefined'
                  && window.matchMedia?.('(hover: none)').matches;
                if (isTouch) return;
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              onBlur={() => setTimeout(() => window.scrollTo(0, 0), 60)}
              placeholder={
                recording ? 'Listening…'
                  : transcribing ? 'Transcribing…'
                  : attachments.length ? 'Add a note about the photos…'
                  : 'Message NEXA…'
              }
              data-testid="nexa-chat-input"
              className="basis-full min-w-0 bg-transparent outline-none resize-none px-2 py-2 text-[16px] min-h-[54px] max-h-56 overflow-y-auto order-first"
            />
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={toggleMax}
              data-testid="nexa-max-toggle"
              title={maxMode ? 'MAX reasoning on — heavyweight model for every message' : 'Turn on MAX reasoning'}
              className={`w-9 h-9 rounded-xl flex flex-col items-center justify-center shrink-0 border transition-colors ${
                maxMode
                  ? 'bg-amber-400/15 border-amber-400/40 text-amber-300 shadow-[0_0_14px_rgba(251,191,36,0.25)]'
                  : 'border-white/10 text-white/35'
              }`}
            >
              <Zap className="w-4 h-4" strokeWidth={2} fill={maxMode ? 'currentColor' : 'none'} />
              <span className="text-[7px] font-mono tracking-widest leading-none mt-0.5">MAX</span>
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={togglePersonality}
              data-testid="nexa-personality-toggle"
              title={personalityMode ? 'Personality mode on — NEXA is fully herself' : 'Turn on Personality mode'}
              className={`w-9 h-9 rounded-xl flex flex-col items-center justify-center shrink-0 border transition-colors ${
                personalityMode
                  ? 'bg-rose-400/15 border-rose-400/40 text-rose-300 shadow-[0_0_14px_rgba(251,113,133,0.25)]'
                  : 'border-white/10 text-white/35'
              }`}
            >
              <Heart className="w-4 h-4" strokeWidth={2} fill={personalityMode ? 'currentColor' : 'none'} />
              <span className="text-[7px] font-mono tracking-widest leading-none mt-0.5">SOUL</span>
            </motion.button>
            <div className="flex-1" />
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={toggleRecord}
              disabled={transcribing}
              data-testid="nexa-chat-mic"
              className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border transition-colors ${
                recording
                  ? 'bg-red-500/20 border-red-400/40 text-red-300 animate-pulse'
                  : 'border-white/10 text-sky-400'
              }`}
              title={recording ? 'Stop recording' : 'Record voice'}
            >
              {transcribing ? (
                <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.8} />
              ) : recording ? (
                <Square className="w-4 h-4" strokeWidth={2.5} fill="currentColor" />
              ) : (
                <Mic className="w-5 h-5" strokeWidth={1.8} />
              )}
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={handleSend}
              disabled={streaming || (!input.trim() && !attachments.length)}
              data-testid="nexa-chat-send"
              className="w-9 h-9 rounded-xl bg-white text-black flex items-center justify-center disabled:opacity-30 shrink-0"
            >
              <ArrowUp className="w-5 h-5" strokeWidth={2.5} />
            </motion.button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {docDialog && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            data-testid="nexa-doc-dialog"
            onClick={() => docDialog.status === 'ready' && cancelDoc()}
          >
            <motion.div
              initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0A0A0A] p-5"
            >
              {docDialog.status === 'error' ? (
                <div className="py-6" data-testid="nexa-doc-dialog-error">
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-rose-400/15 flex items-center justify-center text-rose-300 shrink-0">
                      <X className="w-5 h-5" strokeWidth={2.5} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-white/85">Couldn't add that file</p>
                      <p className="text-[12px] text-white/40 truncate">{docDialog.filename}</p>
                    </div>
                  </div>
                  <p className="text-[13px] text-white/55 leading-snug mb-5">{docDialog.error}</p>
                  <button type="button" onClick={() => setDocDialog(null)}
                    data-testid="nexa-doc-dialog-error-close"
                    className="w-full h-11 rounded-xl bg-white text-black font-semibold text-[14px] active:scale-95 transition-transform">
                    OK
                  </button>
                </div>
              ) : docDialog.status !== 'ready' ? (
                <div className="flex flex-col items-center py-8 gap-3" data-testid="nexa-doc-dialog-loading">
                  <Loader2 className="w-6 h-6 animate-spin text-sky-400" strokeWidth={2} />
                  <p className="text-[13px] text-white/60">
                    {docDialog.status === 'saving' ? 'Saving to her memory…' : 'Reading & suggesting a category…'}
                  </p>
                  <p className="text-[12px] text-white/35 truncate max-w-full">{docDialog.filename}</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-sky-400/15 flex items-center justify-center text-sky-400 shrink-0">
                      <FileText className="w-5 h-5" strokeWidth={2} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-mono tracking-widest text-white/40 uppercase">Add to memory</p>
                      <p className="text-[12px] text-white/40">{docDialog.doc.chunks} sections · she'll remember this</p>
                    </div>
                  </div>

                  <label className="text-[11px] text-white/40">File name</label>
                  <input
                    value={docDialog.filename}
                    onChange={(e) => setDocDialog((d) => ({ ...d, filename: e.target.value }))}
                    data-testid="nexa-doc-dialog-filename"
                    className="w-full mt-1 mb-4 bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 text-[14px] text-white outline-none focus:border-sky-400/50"
                  />

                  <label className="text-[11px] text-white/40">Category</label>
                  <div className="relative mt-1">
                    <FolderOpen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" strokeWidth={2} />
                    <input
                      value={docDialog.category}
                      onChange={(e) => setDocDialog((d) => ({ ...d, category: e.target.value }))}
                      placeholder="e.g. Health, Finance, Levanta"
                      data-testid="nexa-doc-dialog-category"
                      className="w-full bg-white/[0.04] border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-[14px] text-white outline-none focus:border-sky-400/50"
                    />
                  </div>
                  {docDialog.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2.5">
                      {docDialog.categories.map((c) => (
                        <button
                          key={c} type="button"
                          onClick={() => setDocDialog((d) => ({ ...d, category: c }))}
                          data-testid={`nexa-doc-dialog-cat-${c}`}
                          className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                            docDialog.category === c
                              ? 'bg-sky-400/20 border-sky-400/50 text-sky-200'
                              : 'border-white/10 text-white/45 hover:text-white/70'
                          }`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2 mt-6">
                    <button
                      type="button" onClick={cancelDoc}
                      data-testid="nexa-doc-dialog-cancel"
                      className="flex-1 h-11 rounded-xl border border-white/10 text-white/60 text-[14px] active:scale-95 transition-transform"
                    >
                      Cancel
                    </button>
                    <button
                      type="button" onClick={confirmDoc}
                      data-testid="nexa-doc-dialog-save"
                      className="flex-[1.4] h-11 rounded-xl bg-sky-400 text-black font-semibold text-[14px] flex items-center justify-center gap-1.5 active:scale-95 transition-transform"
                    >
                      <Check className="w-4 h-4" strokeWidth={2.5} /> Save to memory
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
